/**
 * Scheduler data: state.ext.scheduler from the store (GET /api/state, then SSE 'ext'), live
 * capacity, and the calls that change it. Live mode calls the hub and re-reads GET /api/scheduler;
 * demo mode (?mock=1) runs the same rules in the browser, starting placed missions through the
 * missions module's demo path.
 */
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { manualStartError, runningWork, schedule, titleFromPrompt, ulid } from '@orgus/protocol';
import type { Mission } from '@orgus/protocol';
import type { EnqueueRequest, Id, ManualStartRequest, ManualStartResponse, MissionsState, NodePool, NodeSettings, PoolInput, QueueEntry, RoomVerdict, SchedulerSettings, SchedulerState, TimeRule } from '@orgus/protocol';
import { ApiError, api, apiGet, toUiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { startMission } from '../missions/data.ts';
import { missionsSlice } from '../missions/model.ts';
import {
  MockRefusal,
  createMockScheduler,
  mockAddRule,
  mockCancel,
  mockCreatePool,
  mockDeletePool,
  mockDeleteRule,
  mockDrain,
  mockEnqueue,
  mockPatchNode,
  mockPatchSettings,
  mockPutProject,
  mockRetry,
  mockSetEntry,
  mockTestAgent,
  mockUpdatePool,
  mockUsageProbe,
} from './mock.ts';
import { SCHEDULER_EXT, liveCapacity, liveRoom, loadsFrom, ownerIndex, schedulerSlice } from './model.ts';
import type { LiveSources } from './model.ts';

const isMock = () => useOrgus.getState().mode === 'mock';
const put = (data: SchedulerState) => useOrgus.getState().applyExt({ ext: SCHEDULER_EXT, type: 'state', data });
const current = (): SchedulerState => schedulerSlice(useOrgus.getState().ext) ?? createMockScheduler(Date.now());

export type SchedulerPhase = 'loading' | 'ready' | 'error' | 'unavailable';

/** The scheduler slice and where it stands. 'unavailable': the hub has no scheduler module. */
export function useSchedulerState(): { state: SchedulerState | null; phase: SchedulerPhase } {
  const { slice, phase, mode } = useOrgus(useShallow((s) => ({ slice: schedulerSlice(s.ext), phase: s.phase, mode: s.mode })));
  useEffect(() => {
    if (mode === 'mock' && phase === 'ready' && !slice) put(createMockScheduler(Date.now()));
  }, [mode, phase, slice]);
  if (phase === 'loading') return { state: null, phase: 'loading' };
  if (phase === 'error') return { state: null, phase: 'error' };
  if (!slice) return { state: null, phase: mode === 'mock' ? 'loading' : 'unavailable' };
  return { state: slice, phase: 'ready' };
}

/** Capacity recomputed from live sessions and load reports (it moves with every patch). */
export function useLiveCapacity(state: SchedulerState | null, now: number) {
  const src = useOrgus(useShallow((s) => ({ machines: s.machines, sessions: s.sessions, checkouts: s.checkouts, ext: s.ext, mode: s.mode })));
  return useMemo(() => (state ? liveCapacity(state, { ...src, loads: loadsFrom(src.ext, now, src.mode === 'mock') }, missionsSlice(src.ext), now) : []), [state, src, now]);
}

/**
 * Where one more session has room, best first, for a project (ADR 0063 step 5). null when the hub
 * has no scheduler (nothing to say, so the dialog says nothing) or while it loads.
 */
export function useRoom(projectId: Id | null, enabled = true): RoomVerdict[] | null {
  const src = useOrgus(useShallow((s) => (enabled ? { machines: s.machines, sessions: s.sessions, checkouts: s.checkouts, ext: s.ext, mode: s.mode } : null)));
  return useMemo(() => {
    if (!src) return null;
    // Recomputed on every store update (sessions, machines, load reports), so no timer of its own.
    const now = Date.now();
    const state = schedulerSlice(src.ext) ?? (src.mode === 'mock' ? createMockScheduler(now) : null);
    if (!state) return null;
    return liveRoom(state, { ...src, loads: loadsFrom(src.ext, now, src.mode === 'mock') }, missionsSlice(src.ext), now, projectId);
  }, [src, projectId]);
}

/**
 * The queue with each waiting entry's reason. The hub writes reasons on every pass; demo mode
 * works them out here with the same `schedule`.
 */
export function useQueue(state: SchedulerState | null, now: number): QueueEntry[] {
  const src = useOrgus(useShallow((s) => ({ machines: s.machines, sessions: s.sessions, checkouts: s.checkouts, ext: s.ext, mode: s.mode })));
  return useMemo(() => {
    if (!state) return [];
    if (src.mode !== 'mock') return state.queue;
    const why = explain(state, { ...src, loads: loadsFrom(src.ext, now, true) }, missionsSlice(src.ext), now);
    return state.queue.map((e) => (why.has(e.id) ? { ...e, ...why.get(e.id)! } : e));
  }, [state, src, now]);
}

function explain(state: SchedulerState, src: LiveSources, missions: MissionsState | null, now: number) {
  const owners = ownerIndex(missions);
  const r = schedule({
    now,
    machines: Object.values(src.machines),
    nodes: state.nodes,
    settings: state.settings,
    running: runningWork(Object.values(src.sessions), state.queue, (id) => owners.get(id) ?? null, now),
    queue: state.queue,
    checkouts: Object.values(src.checkouts),
    ...(src.loads ? { loads: src.loads } : {}),
  });
  const out = new Map<Id, Partial<QueueEntry>>();
  for (const w of r.waiting) out.set(w.entryId, { reason: w.reason, message: w.message });
  for (const p of r.placements) out.set(p.entryId, { reason: null, message: 'A machine has room. It starts on the next pass.' });
  return { has: (id: Id) => out.has(id), get: (id: Id) => out.get(id), placements: r.placements };
}

/** Re-read the slice from the hub. */
export async function refreshScheduler(): Promise<void> {
  if (isMock()) return;
  put(await apiGet<SchedulerState>('/api/scheduler'));
}

async function live<T>(path: string, method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', body?: unknown): Promise<T> {
  const out = await api<T>(path, { method, body: body ?? (method === 'DELETE' ? undefined : {}), timeoutMs: 45_000 });
  await refreshScheduler().catch(() => undefined);
  return out;
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Run a demo change; a refusal becomes the same error shape the hub sends. */
async function demo<T>(what: string, run: () => T, ms = 180): Promise<T> {
  await delay(ms);
  try {
    return run();
  } catch (e) {
    if (e instanceof MockRefusal) throw new ApiError({ what, why: e.message, next: 'Fix it and try again.' });
    throw e;
  }
}

/** Demo mode: place what fits now, starting real demo sessions for missions the demo knows. */
async function demoPass(): Promise<void> {
  const st = useOrgus.getState();
  const s = current();
  const missions = missionsSlice(st.ext);
  const r = explain(s, { ...st, loads: loadsFrom(st.ext, Date.now(), true) }, missions, Date.now());
  let next = s;
  for (const w of next.queue) {
    const x = r.get(w.id);
    if (w.state === 'queued' && x && (x.reason !== undefined || x.message)) next = mockSetEntry(next, w.id, x, Date.now());
  }
  put(next);
  for (const p of r.placements) {
    const e = current().queue.find((x) => x.id === p.entryId);
    if (!e || e.state !== 'queued') continue;
    const name = st.machines[p.machineId]?.name ?? 'the machine';
    put(mockSetEntry(current(), e.id, { state: 'starting', placedOn: p.machineId, checkoutId: p.checkoutId, attempts: e.attempts + 1, message: `Starting on ${name}.` }, Date.now()));
    const known = missions?.missions.some((m) => m.id === e.missionId);
    try {
      const res = known
        ? await startMission(e.missionId, { machineId: p.machineId, checkoutId: p.checkoutId, mode: e.start.mode, model: e.start.model, ...(e.start.note ? { note: e.start.note } : {}), ...(e.start.newWorktree ? { newWorktree: e.start.newWorktree } : {}) })
        : null;
      put(mockSetEntry(current(), e.id, { state: 'started', startedAt: Date.now(), launchId: res?.launch.id ?? null, sessionId: res?.launch.sessionId ?? null, message: `Started on ${name}.` }, Date.now()));
    } catch (err) {
      put(mockSetEntry(current(), e.id, { state: 'failed', message: toUiError(err).why }, Date.now()));
    }
  }
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

export async function enqueue(req: EnqueueRequest): Promise<QueueEntry> {
  if (!isMock()) return live<QueueEntry>('/api/scheduler/queue', 'POST', req);
  const mission = missionsSlice(useOrgus.getState().ext)?.missions.find((m) => m.id === req.missionId);
  const e = await demo('The mission was not queued.', () => {
    const r = mockEnqueue(current(), req, mission, 'owner', Date.now());
    put(r.state);
    return r.entry;
  });
  await demoPass();
  return current().queue.find((x) => x.id === e.id) ?? e;
}

/**
 * A person's start, now or when there is room (POST /api/scheduler/start). Demo mode makes the
 * mission and queues it in the browser; 'now' is never sent here by the dialog (it runs
 * session.start itself), but the API takes it.
 */
export async function manualStart(req: ManualStartRequest): Promise<ManualStartResponse> {
  if (!isMock()) return live<ManualStartResponse>('/api/scheduler/start', 'POST', req);
  const err = manualStartError(req);
  if (err) throw new ApiError({ what: 'The session was not queued.', why: err, next: 'Fix it and try again.' });
  const missionId = `msn_demo_${ulid(Date.now()).slice(-8).toLowerCase()}`;
  const mission = { id: missionId, projectId: req.projectId, title: req.title?.trim() || titleFromPrompt(req.prompt), owner: 'owner', model: req.model ?? null, placement: req.machineId ? { machineId: req.machineId } : {} } as unknown as Mission;
  const e = await demo('The session was not queued.', () => {
    const r = mockEnqueue(current(), { missionId, machineId: req.machineId ?? null, mode: req.mode ?? 'headless', model: req.model ?? null, permissionMode: req.permissionMode ?? null, newWorktree: req.newWorktree ?? null, ...(req.machineId && req.checkoutId ? { checkoutId: req.checkoutId } : {}) }, mission, 'owner', Date.now());
    put(r.state);
    return r.entry;
  });
  await demoPass();
  return { when: 'room', admission: null, entry: current().queue.find((x) => x.id === e.id) ?? e, missionId };
}

export async function cancelEntry(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/scheduler/queue/${encodeURIComponent(id)}`, 'DELETE'));
  await demo('It was not cancelled.', () => put(mockCancel(current(), id, 'owner', Date.now())));
}

export async function retryEntry(id: Id): Promise<QueueEntry> {
  if (!isMock()) return live<QueueEntry>(`/api/scheduler/queue/${encodeURIComponent(id)}/retry`, 'POST');
  await demo('It was not queued again.', () => put(mockRetry(current(), id, Date.now())));
  await demoPass();
  return current().queue.find((x) => x.id === id)!;
}

// ---------------------------------------------------------------------------
// Machines
// ---------------------------------------------------------------------------

export async function setNode(machineId: Id, patch: { labels?: string[] | null; maxSessions?: number | null }): Promise<NodeSettings> {
  if (!isMock()) return live<NodeSettings>(`/api/scheduler/nodes/${encodeURIComponent(machineId)}`, 'PATCH', patch);
  await demo('The change was not saved.', () => put(mockPatchNode(current(), machineId, patch)), 120);
  await demoPass();
  return current().nodes[machineId]!;
}

export async function drainNode(machineId: Id, reason: string): Promise<void> {
  if (!isMock()) return void (await live(`/api/scheduler/nodes/${encodeURIComponent(machineId)}/drain`, 'POST', { reason }));
  await demo('The machine was not drained.', () => put(mockDrain(current(), machineId, true, 'owner', reason, Date.now())));
}

export async function resumeNode(machineId: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/scheduler/nodes/${encodeURIComponent(machineId)}/resume`, 'POST'));
  await demo('The machine was not resumed.', () => put(mockDrain(current(), machineId, false, 'owner', '', Date.now())));
  await demoPass();
}

// ---------------------------------------------------------------------------
// Settings, projects, rules
// ---------------------------------------------------------------------------

export type SettingsPatch = Partial<Pick<SchedulerSettings, 'defaultOffline' | 'utcOffsetMinutes' | 'runtimeBreaker'>> & { agents?: { preferred?: string[]; off?: string[]; spare?: string[] }; limits?: Partial<SchedulerSettings['limits']>; headroom?: Partial<SchedulerSettings['headroom']> };

export async function patchSettings(patch: SettingsPatch): Promise<void> {
  if (!isMock()) return void (await live('/api/scheduler/settings', 'PATCH', patch));
  await demo('The settings were not saved.', () => put(mockPatchSettings(current(), patch)));
  await demoPass();
}

/** Test now (ADR 0089): the machine runs a self-test of this agent; the result arrives in state.selfTests. */
export async function testAgent(machineId: Id, kind: string): Promise<void> {
  if (!isMock()) return void (await live(`/api/scheduler/agents/${encodeURIComponent(machineId)}/${encodeURIComponent(kind)}/test`, 'POST'));
  await demo('The self-test did not start.', () => put(mockTestAgent(current(), machineId, kind, Date.now())), 600);
}

/** Whether machines read Claude Code's usage with a /usage run every 15 minutes (module 'agent-usage'). */
export async function setUsageProbe(on: boolean): Promise<void> {
  if (!isMock()) return void (await live('/api/agent-usage/settings', 'PUT', { probe: on }));
  await demo('The setting was not saved.', () => put(mockUsageProbe(current(), on)));
}

export async function putProject(projectId: Id, patch: { offline?: 'wait' | 'reschedule'; maxSessions?: number | null }): Promise<void> {
  if (!isMock()) return void (await live(`/api/scheduler/projects/${encodeURIComponent(projectId)}`, 'PUT', patch));
  await demo('The project settings were not saved.', () => put(mockPutProject(current(), projectId, patch)));
  await demoPass();
}

export async function addRule(body: { label: string; from: string; to: string; days?: number[]; note?: string }): Promise<TimeRule> {
  if (!isMock()) return live<TimeRule>('/api/scheduler/rules', 'POST', body);
  return demo('The rule was not added.', () => {
    const r = mockAddRule(current(), body, 'owner', Date.now());
    put(r.state);
    return r.rule;
  });
}

export async function deleteRule(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/scheduler/rules/${encodeURIComponent(id)}`, 'DELETE'));
  await demo('The rule was not deleted.', () => put(mockDeleteRule(current(), id)));
  await demoPass();
}

// ---------------------------------------------------------------------------
// Pools
// ---------------------------------------------------------------------------

export async function createPool(body: PoolInput): Promise<NodePool> {
  if (!isMock()) return live<NodePool>('/api/scheduler/pools', 'POST', body);
  return demo('The pool was not created.', () => {
    const r = mockCreatePool(current(), body, 'owner', Date.now());
    put(r.state);
    return r.pool;
  });
}

export async function updatePool(id: Id, patch: Partial<PoolInput>): Promise<void> {
  if (!isMock()) return void (await live(`/api/scheduler/pools/${encodeURIComponent(id)}`, 'PATCH', patch));
  await demo('The pool was not changed.', () => put(mockUpdatePool(current(), id, patch)));
}

export async function deletePool(id: Id): Promise<void> {
  if (!isMock()) return void (await live(`/api/scheduler/pools/${encodeURIComponent(id)}`, 'DELETE'));
  await demo('The pool was not deleted.', () => put(mockDeletePool(current(), id)));
}
